import { useCallback, useState } from 'react'

import { type AskedFor, decide, whatIsAsked } from '../../api/assistants'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { useAnnounce } from '../../ui/Announce'
import { Button } from '../../ui/Control'
import { Failed, Waiting } from '../../ui/Status'
import { Switch } from '../../ui/Switch'

export interface AllowProps {
  /** The request the assistant started, out of the address it sent the browser to. */
  readonly request: string
  readonly onDone: () => void
}

/**
 * Where somebody decides whether a program may reach their space.
 *
 * This is the only screen in MyLib that answers a question somebody else asked, and everything about
 * it follows from that.
 *
 * **What is being handed over is said in plain words, before the buttons.** Not "grant access" — a
 * list of what the assistant would be able to see, because "my whole library, my notes, my
 * timetable" is the thing being decided and nobody reading a two-word label would decide it.
 *
 * **The program's name is shown as its own claim.** It registered itself; MyLib has not checked and
 * cannot. Presenting it as a fact would be MyLib vouching for something it knows nothing about.
 *
 * **Reading is the starting position.** The switch begins off, so the safe half of the choice is the
 * one that happens if somebody stops reading and presses the obvious button.
 *
 * **Refusing is a real answer, not a way out.** It is a control of the same size beside the other
 * one, and the program is told — rather than being left waiting for a browser that never comes back.
 */
export function Allow({ request, onDone }: AllowProps) {
  const asked = useLoad(
    useCallback((signal: AbortSignal) => whatIsAsked(request, signal), [request]),
  )
  const [name, setName] = useState('')
  const [mayWrite, setMayWrite] = useState(false)
  const [working, setWorking] = useState(false)
  const [refused, setRefused] = useState<Failure | null>(null)
  const announce = useAnnounce()

  const answer = async (approve: boolean) => {
    setWorking(true)
    setRefused(null)
    try {
      const where = await decide(request, {
        approve,
        name: name.trim(),
        mayWrite,
      })
      announce(approve ? 'Connected.' : 'Refused.')
      /*
        The browser leaves for the program's own address, and it leaves because somebody pressed
        something. Replacing the entry means the back button does not land on a request that has
        just been answered and can never be answered again.
      */
      window.location.replace(where)
    } catch (failure) {
      setRefused(asFailure(failure))
      setWorking(false)
    }
  }

  if (asked.load.status === 'waiting') {
    return <Waiting label="Looking at what is being asked" />
  }
  if (asked.load.status === 'failed') {
    return (
      <div className="allow">
        <Failed failure={asked.load.failure} title="Nothing to answer" onRetry={asked.retry} />
        <div className="controls">
          <Button onClick={onDone}>Back to MyLib</Button>
        </div>
      </div>
    )
  }

  const who: AskedFor = asked.load.value
  const suggested = name.trim() === '' ? who.clientName : name.trim()

  return (
    <div className="allow">
      <h2 className="allow__title">{who.clientName} would like to reach this space</h2>
      <p className="allow__lead">
        That is the name the program gave for itself. MyLib has no way of checking it — only connect
        something you started yourself, just now.
      </p>

      <ul className="allow__points">
        <li>
          It can search and read everything in this space: documents, notes, tasks, flashcards,
          subjects and your timetable.
        </li>
        <li>It sees exactly what you see. Nothing more, and never another space.</li>
        <li>You can revoke it at any time, and what it wrote stays.</li>
      </ul>

      <div className="field">
        <label className="field__label" htmlFor="allow-name">
          Call it
        </label>
        <input
          id="allow-name"
          className="field__control"
          value={name}
          placeholder={who.clientName}
          maxLength={120}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />
        <p className="field__hint">
          So you can tell it apart later — “Claude on the iPad”, “Claude on the laptop”.
        </p>
      </div>

      <Switch
        label="May also write"
        hint="Off, it can only read. On, it can also make notes, tasks, flashcards and documents here."
        on={mayWrite}
        onChange={setMayWrite}
        disabled={working}
      />

      {refused !== null && <Failed failure={refused} title="That did not work" />}

      <div className="controls">
        <Button
          tone="primary"
          disabled={working}
          onClick={() => {
            void answer(true)
          }}
        >
          {working ? 'Connecting…' : `Allow ${suggested}`}
        </Button>
        <Button
          disabled={working}
          onClick={() => {
            void answer(false)
          }}
        >
          No
        </Button>
      </div>
    </div>
  )
}
